// _forms/_shared/utils/validators.js
/**
 * Validadores puros - Sin dependencias de React
 * Todas las funciones reciben datos y devuelven boolean o mensaje de error
 */
export const validators = {
  /**
   * Validar que un campo no esté vacío
   */
  isRequired: (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    return true;
  },

  /**
   * Validar que sea un número entero
   */
  isInteger: (value) => {
    if (value === null || value === undefined) return true; // Permitir vacío
    return /^\d+$/.test(value.toString());
  },

  /**
   * Validar que sea un email válido
   */
  isEmail: (value) => {
    if (!value || value.trim() === '') return true; // Permitir vacío
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  },

  /**
   * Validar fecha
   */
  isValidDate: (value) => {
    if (!value) return true; // Permitir vacío
    const date = new Date(value);
    return !isNaN(date.getTime());
  },

  /**
   * Validar campo específico según su tipo
   */
  validateField: (field, value) => {
    const errors = [];
    if (field.required && !validators.isRequired(value)) {
      errors.push('Este campo es obligatorio.');
    }
    if (field.dataType === 'integer' && !validators.isInteger(value)) {
      errors.push('Este campo solo acepta números.');
    }
    if (field.dataType === 'email' && !validators.isEmail(value)) {
      errors.push('Por favor, ingrese un correo electrónico válido.');
    }
    return errors;
  },

  /**
   * Validar todos los campos de un formulario
   */
  validateFormFields: (fields, isFillingForm = true) => {
    if (!fields || !Array.isArray(fields)) return { valid: true, errors: [] };
    const errors = {};
    let hasErrors = false;
    fields.forEach(field => {
      if (field.isPackageInclude) return;
      if (!isFillingForm) return;
      const fieldErrors = validators.validateField(field, field.value);
      if (fieldErrors.length > 0) {
        errors[field.id] = fieldErrors;
        hasErrors = true;
      }
    });
    return { valid: !hasErrors, errors };
  },

  /**
   * Obtener mensaje de error para un campo
   */
  getFieldErrorMessage: (field) => {
    if (!field.invalid) return '';
    return field.errorMessage || 'Campo inválido';
  },

  /**
   * Verificar si un campo es visible (sin condiciones)
   */
  isFieldVisible: (field, shouldRenderFn) => {
    if (!shouldRenderFn) return true;
    return shouldRenderFn(field);
  },

  /**
   * Validar que el deslinde esté aceptado
   */
  isDeslindeAccepted: (aceptaDeslinde) => {
    return aceptaDeslinde === true;
  },

  /**
   * Validar documento (CI, cédula, etc.)
   */
  validateDocument: (value) => {
    if (!value) return { valid: true };
    const clean = value.replace(/[^0-9]/g, '');
    if (clean.length < 6 || clean.length > 15) {
      return {
        valid: false,
        message: 'El documento debe tener entre 8 y 15 dígitos'
      };
    }
    return { valid: true };
  },

  /**
   * Validar teléfono
   */
  validatePhone: (value) => {
    if (!value) return { valid: true };
    const clean = value.replace(/[^0-9+\-() ]/g, '');
    if (clean.length < 7 || clean.length > 20) {
      return {
        valid: false,
        message: 'El teléfono debe tener entre 7 y 20 dígitos'
      };
    }
    return { valid: true };
  },

  /**
   * Validar campo específico con reglas extendidas
   */
  validateFieldExtended: (field, value) => {
    const errors = [];
    // Ignorar validaciones en banners informativos
  if (field.type === 'banner') {
    return errors;
  }
    const labelLower = field.label?.toLowerCase() || '';

    if (field.required && !validators.isRequired(value)) {
      errors.push('Este campo es obligatorio.');
    }
    if (field.dataType === 'integer' && value && !validators.isInteger(value)) {
      errors.push('Este campo solo acepta números.');
    }
    if (field.dataType === 'email' && value && !validators.isEmail(value)) {
      errors.push('Por favor, ingrese un correo electrónico válido.');
    }

    const esCampoDocumento =
      labelLower.includes('cédula') ||
      labelLower.includes('documento') ||
      labelLower.includes('carnet') ||
      labelLower === 'ci' ||
      labelLower.includes('identidad');
    if (esCampoDocumento) {
      const docValidation = validators.validateDocument(value);
      if (!docValidation.valid) {
        errors.push(docValidation.message);
      }
    }

    if (
      labelLower.includes('teléfono') ||
      labelLower.includes('celular') ||
      labelLower === 'telefono'
    ) {
      const phoneValidation = validators.validatePhone(value);
      if (!phoneValidation.valid) {
        errors.push(phoneValidation.message);
      }
    }

    return errors;
  }
};

export default validators;